import type { components } from '@/types/openapi';

export enum ConsentStatus {
  CONNECTED = 'connected',
  NOT_CONNECTED = 'not_connected',
  INSUFFICIENT_PERMISSIONS = 'insufficient_permissions',
  ERROR = 'error'
}

export enum ResponseActionStatus {
  COMPLETED = 'completed',
  ERROR = 'error',
  FAILED = 'failed',
  SKIPPED = 'skipped'
}

export enum AppType {
  ENTRA_IDENTITY_LOG_INGESTION = 'entra_identity_log_ingestion',
  ENTRA_IDENTITY_RESPONSE_ACTIONS = 'entra_identity_response_actions',
  ENTRA_AND_DEFENDER_LOG_INGESTION = 'entra_and_defender_log_ingestion',
  ENTRA_AND_DEFENDER_RESPONSE_ACTIONS = 'entra_and_defender_response_actions',
  ENTRA_IDENTITY_INVENTORY_READ = 'entra_identity_inventory_read'
}

export enum AppCategory {
  INGESTION = 'ingestion',
  RESPONSE = 'response',
  INVENTORY = 'inventory'
}

export type ResponseActionSummaryDTO =
  components['schemas']['ResponseActionSummaryItem'];

export type ResponseActionHealthStats = {
  total: number;
  succeeded: number;
  failed: number;
  lastSuccessDate: string | null;
};

export type MicrosoftIntegrationData = {
  app_type: AppType;
  api_token_status?: ConsentStatus;
  last_api_token_status_check_error?: string;
  api_action_status?: ConsentStatus;
  last_api_action_status_check_error?: string;
  response_actions?: ResponseActionSummaryDTO[] | null;
};

type IntegrationResponse = components['schemas']['IntegrationResponse'];

export type IntegrationDeleteResponse =
  components['schemas']['IntegrationDeleteResponse'];

export type MicrosoftIntegration = Omit<
  IntegrationResponse,
  'integration_data' | 'integration_type'
> & {
  integration_type: 'microsoft';
  integration_data: MicrosoftIntegrationData;
};

export type LegacyLimaConnection =
  components['schemas']['LegacyLimaConnectionResponse'];

export type MicrosoftIntegrationsByAppType = Partial<
  Record<AppType, MicrosoftIntegration[]>
>;

export type AggregateConsentStatus = {
  status: ConsentStatus;
  label: string;
};

export type ConsentEligibility = {
  isEligible: boolean;
  requirement: string;
};

export type ConsentTileConfig = {
  appType: AppType;
  category: AppCategory;
  title: string;
  description: string;
  alternativeGuidance?: string;
  capabilities: string[];
  requestedScopes: string[];
  requirementNote?: string;
};

export type StepConfig = {
  category: AppCategory.INGESTION | AppCategory.RESPONSE;
  label: string;
  heading: string;
  instruction: string;
  options: ConsentTileConfig[];
};

export type TenantCategoryState = {
  tenantId: string;
  integrations: MicrosoftIntegration[];
  hasConflict: boolean;
};

export type TenantCategoryStateById = Record<string, TenantCategoryState>;

export type ConsentTarget = {
  targetTile: ConsentTileConfig;
  existingIntegration?: MicrosoftIntegration;
};

export type DisconnectTarget = {
  tile: ConsentTileConfig;
  integration: MicrosoftIntegration;
};

export type PendingConsent = {
  appType: AppType;
  existingIntegrationId?: number;
};

export type ConsentTileProps = {
  tile: ConsentTileConfig;
  connections: MicrosoftIntegration[];
  legacyConnections: LegacyLimaConnection[];
  allIntegrations: MicrosoftIntegration[];
  categoryStateByTenant: TenantCategoryStateById;
  hasAdminUpdate: boolean;
  isServerStateAvailable: boolean;
  hasAnyDefenderIngestion: boolean;
  consentEligibility: ConsentEligibility;
  pendingConsent: PendingConsent | null;
  consentErrorTarget: PendingConsent | null;
  onConsent: (target: ConsentTarget) => void;
  onDisconnect: (integration: MicrosoftIntegration) => void;
  disconnectingIntegrationId?: number;
  disconnectErrorIntegrationId?: number;
};
